import React from "react";
import { Button } from "@/components/ui/button";
import { Calendar, Table2, Upload, Sparkles, RefreshCw } from "lucide-react";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { BulkAddSessionsButton } from "./BulkAddSessionsButton";
import { useAuth } from "@/context/AuthContext";

interface SessionsViewToggleProps {
  viewMode: "calendar" | "table";
  onViewModeChange: (value: "calendar" | "table") => void;
  onAddSession: () => void;
  onBulkAddSessions?: () => void;
  onRefresh?: () => void;
  onPublishToOffers?: () => void;
  isAdmin?: boolean;
  isPublishing?: boolean;
  isInvitedUser?: boolean;
  onAIFetch?: () => void;
  onCleanSessions?: () => void;
  /** When true, hide Publish/Fetch/Bulk add/Add session (e.g. org-level sessions tab). */
  hideActionButtons?: boolean;
  /** When true, show Clean button even when hideActionButtons is true. */
  showCleanButton?: boolean;
}

export const SessionsViewToggle: React.FC<SessionsViewToggleProps> = ({
  viewMode,
  onViewModeChange,
  onAddSession,
  onBulkAddSessions,
  onRefresh,
  onPublishToOffers,
  isAdmin = false,
  isPublishing = false,
  isInvitedUser = false,
  onAIFetch,
  onCleanSessions,
  hideActionButtons = false,
  showCleanButton = false
}) => {
  // Get user auth to check for admin_user role
  const { userProfile } = useAuth();
  const isAdminUser = userProfile?.role === 'admin_user';

  return (
    <div className="flex justify-between items-center">
      <div className="flex items-center space-x-2">
        <Tabs 
          value={viewMode} 
          onValueChange={(value) => onViewModeChange(value as "calendar" | "table")}
          className="w-auto"
        >
          <TabsList>
            <TabsTrigger value="calendar">
              <Calendar className="h-4 w-4 mr-2" />
              Calendar view
            </TabsTrigger>
            <TabsTrigger value="table">
              <Table2 className="h-4 w-4 mr-2" />
              Table view
            </TabsTrigger>
          </TabsList>
        </Tabs>
        {onRefresh && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            title="Refresh"
            aria-label="Refresh"
            onClick={onRefresh}
          >
            <RefreshCw className="h-4 w-4" />
          </Button>
        )}
      </div>
      
      <div className="flex items-center space-x-2">
        {hideActionButtons ? null : (
          <>
        {/* Show "Publish to offers" button only for admin users, not for invited users */}
        {isAdmin && !isInvitedUser && onPublishToOffers && (
          <Button 
            onClick={onPublishToOffers} 
            variant="outline" 
            size="sm"
            disabled={isPublishing}
            className="flex items-center gap-1"
          >
            <Upload className="h-4 w-4" />
            {isPublishing ? "Publishing..." : "Publish to offers"}
          </Button>
        )}

        {/* AI-powered data fetch button - only for admin_user role */}
        {isAdminUser && onAIFetch && (
          <Button 
            onClick={onAIFetch} 
            variant="outline" 
            size="sm"
            className="flex items-center gap-1"
          >
            <Sparkles className="h-4 w-4" />
            Fetch
          </Button>
        )}
        
        {/* Always show Bulk Add Sessions button when the handler is available */}
        {onBulkAddSessions && (
          <BulkAddSessionsButton onClick={onBulkAddSessions} />
        )}
        
        <Button onClick={onAddSession} size="sm">
          Add session
        </Button>
          </>
        )}
        {(!hideActionButtons || showCleanButton) && isAdminUser && onCleanSessions && (
          <Button onClick={onCleanSessions} variant="outline" size="sm">
            Clean
          </Button>
        )}
      </div>
    </div>
  );
};
